import Delta from "quill-delta";
import { AI_AGENT_STATIC_PATH_V2 } from "@/api/staticPaths";
import { canv } from "@/canv/global";
import {
    drawRoundedRect,
    getBoolProperty,
    getNumberProperty,
    getStringProperty,
} from "@/canv/utils";
import { elementBase } from "@/elementBase/elementBase";
import { Group } from "@/group";
import type { CreateSubMenuActions } from "@/interface/WidgetMenu";
import { selectionManager } from "@/tools/cursorTool";
import type { textWidget } from "../text/text";
import {
    createTextPartByEntityType,
    ensureTextPartByEntityType,
    isTextWidgetV2,
    isTextWidgetV2Editing,
    regenerateTextPartUID,
    resolveTextPartEntityTypeFromState,
} from "../text/textPartFactory";
import { TextWidgetV2 } from "../text/textV2";
import {
    AI_ANSWER_GAP,
    AI_ANSWER_PADDING,
    AI_ICON_HEGHT,
    AI_TITLES,
    LOADING_ANIMATION_HEIGHT,
    LOADING_ANIMATION_PADDING,
    LOADING_ANIMATION_WIDTH,
} from "./constants";
import { LoadingAnimation } from "./loadingAnimation";
import type { InteractionTypes, SearchTextObject, WidgetStateMap } from "./types";

export class AiAnswer extends elementBase {
    constructor(startX = 0, startY = 0, action: CreateSubMenuActions = "retro_summary") {
        super();

        this.startX = startX;
        this.startY = startY - AI_ANSWER_GAP - AI_ICON_HEGHT;

        this.textTitle = createTextPartByEntityType("textWidgetV2");
        this.textTitle.setText(AI_TITLES[action]);
        this.textTitle.menuTextSize(20);
        this.setupTextPart(this.textTitle);

        this.generatedText = createTextPartByEntityType("textWidgetV2");
        this.setupTextPart(this.generatedText);

        this.aiIcon = new Image();
        this.aiIcon.src = `${canv.host}${AI_AGENT_STATIC_PATH_V2}widget-icon.svg`;

        this.loadingAnimation = new LoadingAnimation(
            `${canv.host}${AI_AGENT_STATIC_PATH_V2}loading_dots.json`,
            LOADING_ANIMATION_WIDTH,
            LOADING_ANIMATION_HEIGHT,
        );
        this.loadingAnimation.init();

        this.nothingWhenInteraction = true;

        this.updateDims();
    }

    private setupTextPart(textPart: textWidget | TextWidgetV2) {
        if (textPart instanceof TextWidgetV2) {
            textPart.setResizeMode("shape");
        } else {
            textPart.shapeMode = true;
        }

        textPart.setKeepMinWidth(false);
    }

    regenerateUIDS(): void {
        regenerateTextPartUID(this.textTitle, this.UID);
        regenerateTextPartUID(this.generatedText, this.UID);
    }

    updateDims() {
        const isTextV2Editing = isTextWidgetV2Editing(this.generatedText);
        const getLineHeight = (textPart: textWidget | TextWidgetV2) => {
            return isTextWidgetV2(textPart) ? textPart.lineHeight : textPart.lines[0].textHeight;
        };
        this.textTitle.hardWidth = this.width - 2 * AI_ANSWER_PADDING;
        this.textTitle.Fit();
        this.textTitle.startX = this.startX + AI_ANSWER_PADDING;
        this.textTitle.startY =
            this.startY +
            AI_ICON_HEGHT +
            AI_ANSWER_GAP +
            AI_ANSWER_PADDING +
            getLineHeight(this.textTitle);
        this.textTitle.updateDims();

        this.generatedText.hardWidth = this.width - 2 * AI_ANSWER_PADDING;
        this.generatedText.menuTextSize(14);
        this.generatedText.startX = this.startX + AI_ANSWER_PADDING;
        this.generatedText.startY =
            this.textTitle.startY + AI_ANSWER_GAP + getLineHeight(this.generatedText);

        if (!isTextV2Editing) {
            this.generatedText.Fit();
            this.generatedText.updateDims();
        }

        const newShapeHeight =
            2 * AI_ANSWER_PADDING +
            this.textTitle.height +
            AI_ANSWER_GAP +
            this.generatedText.height;
        this.shapeHeight = this.isLoading
            ? Math.max(this.shapeHeight, newShapeHeight)
            : newShapeHeight;

        this.height = this.shapeHeight + AI_ANSWER_GAP + AI_ICON_HEGHT;

        this.updateBase(this.startX, this.startY, this.width, this.height);
    }

    draw() {
        if (this.aiIcon.complete && this.aiIcon.naturalWidth > 0) {
            canv.ctx.drawImage(this.aiIcon, this.startX, this.startY);
        }

        drawRoundedRect(
            this.startX,
            this.startY + this.aiIcon.height + AI_ANSWER_GAP,
            this.width,
            this.shapeHeight,
            16,
        );
        canv.ctx.fillStyle = "#D2ECFF";
        canv.ctx.fill();

        this.textTitle.draw();

        if (this.isLoading && this.loadingAnimation) {
            this.loadingAnimation.draw(
                this.generatedText.startX - LOADING_ANIMATION_PADDING,
                this.textTitle.startY + AI_ANSWER_GAP - LOADING_ANIMATION_PADDING,
            );
        } else {
            this.generatedText.draw();
        }
    }

    async TryAction(type: string) {
        if (this.interactionType === "generatedTextFocus") {
            await this.generatedText.TryAction(type);

            this.updateDims();

            if (type === "keyboardDown") {
                this.tryShortcut();

                return;
            }

            if (type === "mousedown" && !this.generatedText.isOver()) {
                this.stopInteraction();
                canv.toolBox.TryGetToolByName("cursorTool").turnMode("");
                canv.toolBox.TryGetToolByName("cursorTool").TryAction("mousedown");
            }
        }
    }

    tryShortcut() {
        if (canv.keyboard.isEscCommand()) {
            this.stopInteraction();
            canv.toolBox.TryGetToolByName("cursorTool").turnMode("");
        }
    }

    isInteraction(context: string, force = false) {
        if (context === "focus" || (context === "" && force)) {
            if (force || this.generatedText.isOver()) {
                this.fixState("generatedTextFocus");
                this.interactionType = "generatedTextFocus";
                this.generatedText.focusText();
                return { type: "interaction" };
            }
        }

        if (context === "insertText" || context === "pastText") {
            this.fixState("generatedTextFocus");
            this.interactionType = "generatedTextFocus";

            if (canv.keyboard.isEnter()) {
                this.fixState("generatedTextFocus");
                this.interactionType = "generatedTextFocus";

                this.generatedText.focusTextByEnter();
            } else if (!canv.keyboard.isEnter()) {
                this.fixState("generatedTextFocus");
                this.interactionType = "generatedTextFocus";

                this.generatedText.focusText();
                this.generatedText.selection.selectAll();
                this.generatedText.processDelete();

                this.TryAction("keyboardDown");
            }

            this.updateDims();

            return {
                type: "interaction",
            };
        }

        return { type: "none" };
    }

    stopInteraction() {
        if (this.interactionType === "generatedTextFocus") {
            this.generatedText.unfocusText();
            canv.eventManager.openRecord();
            this.saveState("generatedTextFocus");
            canv.eventManager.closeRecord();

            if (!this.generatedText.aiGenerated) {
                this.destroyWidget();
            }
        }

        this.interactionType = "default";
    }

    translate(x: number, y: number) {
        this.startX += x;
        this.startY += y;

        this.updateDims();
    }

    searchText(text: string) {
        const result: SearchTextObject[] = [];
        const textWidget = this.generatedText.getText().toLowerCase();

        if (textWidget.includes(text)) {
            result.push({
                status: true,
                textWidget: this.generatedText,
                mode: "widget",
            });
        }

        return result;
    }

    destroyWidget() {
        const lineHeight = isTextWidgetV2(this.generatedText)
            ? this.generatedText.lineHeight
            : this.generatedText.lines[0].textHeight;

        const startY = this.startY + AI_ICON_HEGHT + AI_ANSWER_GAP + lineHeight;
        const newTextWidget = new TextWidgetV2(this.startX, startY);
        newTextWidget.setResizeMode("shape");
        const group = new Group(true);

        newTextWidget.setParent(group);
        canv.canvasController.addGroup(group);

        if (isTextWidgetV2(this.generatedText)) {
            const data = this.generatedText.getData();
            newTextWidget.setData({ ...data, delta: new Delta(data.delta) });
            newTextWidget.calculateLineHeight();
        } else {
            //Нужен скрипт для v1 => v2
            /* const linesFromWidget = [...this.generatedText.lines];

            linesFromWidget.forEach((line) => {
                line.parent = newTextWidget;
            });

            newTextWidget.lines = linesFromWidget; */
        }

        newTextWidget.updateDims();

        canv.eventManager.openRecord();
        canv.canvasController.deleteProcessor(this.parent);
        canv.eventManager.createBuildEvent("create", group);
        canv.eventManager.closeRecord();

        selectionManager.setSelected([], false);
    }

    getWidgetOptions() {
        const widgetOptions = [];

        return widgetOptions;
    }

    getFixType(reason: string) {
        let fixType = "full";

        switch (reason) {
            case "move":
                fixType = "move";
                break;

            case "resize":
                fixType = "resize";
                break;

            case "bold":
            case "generatedTextFocus":
                fixType = "bold";
                break;

            default:
                return fixType;
        }

        return fixType;
    }

    getState<FixType extends keyof WidgetStateMap>(fixType: FixType): WidgetStateMap[FixType] {
        const state: WidgetStateMap = {
            move: {
                startX: this.startX,
                startY: this.startY,
            },
            resize: {
                startX: this.startX,
                startY: this.startY,
                width: this.width,
            },
            bold: {
                generatedText: this.generatedText.getState(
                    "full",
                ) as WidgetStateMap["bold"]["generatedText"],
            },
            full: {
                UID: this.UID,
                parentUID: this.parentUID,
                entityType: "aiAnswer",
                startX: this.startX,
                startY: this.startY,
                width: this.width,
                isLoading: this.isLoading,
                textTitle: this.textTitle.getState("full") as WidgetStateMap["full"]["textTitle"],
                generatedText: this.generatedText.getState(
                    "full",
                ) as WidgetStateMap["full"]["generatedText"],
            },
        };

        return state[fixType];
    }

    loadState<FixType extends keyof WidgetStateMap>(
        state: WidgetStateMap[FixType],
        fixType: FixType,
    ) {
        switch (fixType) {
            case "move":
                this.startX = getNumberProperty("startX", state);
                this.startY = getNumberProperty("startY", state);

                break;

            case "resize":
                this.startX = getNumberProperty("startX", state);
                this.startY = getNumberProperty("startY", state);
                this.width = getNumberProperty("width", state);

                break;

            case "bold": {
                const currentState = state as WidgetStateMap["bold"];
                this.generatedText = ensureTextPartByEntityType(
                    this.generatedText,
                    resolveTextPartEntityTypeFromState(currentState.generatedText),
                );
                this.setupTextPart(this.generatedText);
                this.generatedText.loadState(currentState.generatedText, "full");

                break;
            }

            case "full": {
                const currentState = state as WidgetStateMap["full"];

                this.UID = getStringProperty("UID", currentState);
                this.parentUID = getStringProperty("parentUID", currentState);

                this.startX = getNumberProperty("startX", currentState);
                this.startY = getNumberProperty("startY", currentState);
                this.width = getNumberProperty("width", currentState);

                this.isLoading = getBoolProperty("isLoading", currentState);

                this.textTitle = ensureTextPartByEntityType(
                    this.textTitle,
                    resolveTextPartEntityTypeFromState(currentState.textTitle),
                );
                this.setupTextPart(this.textTitle);
                this.generatedText = ensureTextPartByEntityType(
                    this.generatedText,
                    resolveTextPartEntityTypeFromState(currentState.generatedText),
                );
                this.setupTextPart(this.generatedText);
                this.textTitle.loadState(currentState.textTitle, "full");
                this.generatedText.loadState(currentState.generatedText, "full");

                if (!this.isLoading && this.loadingAnimation) {
                    this.isLoading = false;
                    this.loadingAnimation.destroy();
                    this.loadingAnimation = null;
                }
            }
        }

        this.updateDims();
    }

    constructorName: "aiAnswer" = "aiAnswer";
    interactionType: InteractionTypes = "default";
    startX = 0;
    startY = 0;
    width = 560;
    height = 0;
    shapeHeight = 218;
    isLoading = true;

    textTitle: textWidget | TextWidgetV2;
    generatedText: textWidget | TextWidgetV2;
    aiIcon: HTMLImageElement;
    loadingAnimation: LoadingAnimation | null;
}
